// Canvas 2-D drawing of an IP2 scene (cIP2_2DP::Render and the subclasses): every active item
// (m_bActive && m_bParentActive) in layer order, stable in tree order inside a layer (the
// primitive system sorts its sub-primitives by layer). Quads are centred on the item's world
// translation; a cleared m_bNormalBlend draws additively. Text follows FM_Font::PrintAt
// 0xf24d30: the first glyph's pen at the alignment box's left (FM_Font::__ComputeAlignment
// 0xf24400: left, centred or right of the item's rect, truncated to whole pixels), the baseline
// on the rect's lower edge, or half the font's cached glyph height below the rect's centre when
// m_bVerticalAlignment is set.
import { DESIGN_H, DESIGN_W, T_BUTTON, T_CRAFT, T_POLY, T_TAB, T_TEX, T_TICK, T_TXT, T_WPCELL, T_XOM, type Item, type Rgba, type Scene, type TexRef, type UiData } from './ip2.ts';

/** Style-guide class of a widget type (cIP2_2DSystem::m_StyleGuide lookups). */
const STYLE_CLASS: Record<number, string> = { [T_BUTTON]: 'BUTTON', [T_TAB]: 'TABBUTTON', [T_TICK]: 'TICKBOX', [T_CRAFT]: 'CRAFTBUTTON' };
const INITIAL_STATE: Record<string, string> = { BUTTON: 'NotFocused', TABBUTTON: 'NotSelected', TICKBOX: 'Unticked', CRAFTBUTTON: 'NotFocused' };

/** The textures a widget item's style may draw (every state's), for preloading. */
export function widgetTextures(ui: UiData, it: Item): string[] {
  const cls = STYLE_CLASS[it.type];
  return cls ? ui.styleTextures(cls, it.style) : [];
}

export class Ip2Painter {
  ctx: CanvasRenderingContext2D;
  ui: UiData;
  /** Design space -> canvas pixels. */
  s = 1; ox = 0; oy = 0;
  private tint = new Map<string, HTMLCanvasElement>();
  private heights = new Map<string, number>();
  private advances = new Map<string, number>();
  /** Draws an XOM item (cIP2_2DXom: its capture composited at its quad) in its place of the layer
   * order; the host owns the 3-D side. */
  xom: ((it: Item, box: [number, number, number, number]) => void) | null = null;

  constructor(ctx: CanvasRenderingContext2D, ui: UiData) {
    this.ctx = ctx;
    this.ui = ui;
  }

  /** Fits the 1920x1080 design space into a w x h canvas (centred, uniform scale). */
  viewport(w: number, h: number): void {
    this.s = Math.min(w / DESIGN_W, h / DESIGN_H);
    this.ox = (w - DESIGN_W * this.s) / 2;
    this.oy = (h - DESIGN_H * this.s) / 2;
  }

  /** Canvas position of a design-space point (origin at the centre, y up). */
  toCanvas(x: number, y: number): { x: number; y: number } {
    return { x: this.ox + (x + DESIGN_W / 2) * this.s, y: this.oy + (DESIGN_H / 2 - y) * this.s };
  }

  /** Draws `scene` (placed) restricted to the subtrees of `roots` when given. */
  draw(scene: Scene, roots?: Item[]): void {
    const order: Item[] = [];
    const walk = (it: Item): void => {
      if (!it.pActive) return;
      order.push(it);
      for (const c of it.children) walk(c);
    };
    for (const r of roots ?? scene.roots) walk(r);
    order.sort((a, b) => a.layer - b.layer);
    for (const it of order) this.item(it);
  }

  /** An item's quad in canvas pixels: left, top, width, height. */
  boxOf(it: Item): [number, number, number, number] {
    const c = this.toCanvas(it.world[4], it.world[5]);
    return [c.x - (it.pw * this.s) / 2, c.y - (it.ph * this.s) / 2, it.pw * this.s, it.ph * this.s];
  }

  private item(it: Item): void {
    const ctx = this.ctx;
    const box = this.boxOf(it);
    ctx.save();
    if (!it.normalBlend) ctx.globalCompositeOperation = 'lighter';
    if (Math.abs(it.pAngle) > 0.01) {
      ctx.translate(box[0] + box[2] / 2, box[1] + box[3] / 2);
      ctx.rotate((-it.pAngle * Math.PI) / 180);
      ctx.translate(-(box[0] + box[2] / 2), -(box[1] + box[3] / 2));
    }
    switch (it.type) {
      case T_POLY:
        this.poly(box, it.pColours);
        break;
      case T_TEX: {
        const t = this.ui.texture(it.tex ?? it.resId);
        if (t) this.texture(t, box, it.pColours);
        break;
      }
      case T_TXT: {
        const tx = it.sections.txt ?? {};
        const text = it.text ?? this.ui.localise(tx.m_LocaliseToken as string, tx.m_string as string);
        if (text) {
          const shadow = tx.m_shadow ? { dx: Number(tx.m_shadow_offsetx ?? 0), dy: Number(tx.m_shadow_offsety ?? 0), col: ['r', 'g', 'b', 'a'].map(c => Number(tx[`m_shadow_colour_${c}`] ?? 0)) as Rgba } : null;
          this.text(text, Number(tx.m_FontID ?? 0), box, it.pColours[0], Number(tx.m_Align ?? 0), !!tx.m_bVerticalAlignment, !!tx.m_wrappable, Number(tx.m_nLineSpace ?? 0), shadow, Math.hypot(it.world[0], it.world[1]));
        }
        break;
      }
      case T_TICK: case T_BUTTON: case T_TAB: case T_CRAFT:
        this.widget(it, box);
        break;
      case T_WPCELL: {
        const g = it.extra ?? {};
        for (const k of ['cell', 'icon', 'focus']) {
          const ref = g[k] as number | string | undefined;
          const t = ref !== undefined ? this.ui.texture(ref) : null;
          if (t) this.texture(t, box, it.pColours);
        }
        break;
      }
      case T_XOM:
        this.xom?.(it, box);
        break;
      default:
        break;
    }
    ctx.restore();
  }

  /** A widget's style texture and title. cIP2_2DButton::__DrawTitle 0xf78d20 (buttons, crafting
   * buttons): the title colour is the style state's font colour unless `m_bOverrideColour`
   * (SetTitleColour), wrapped in an alignment box 0.85 of the width wide (l 0.05 w, r 0.9 w;
   * __ComputeAlignment uses its size only) around the item's centre raised by 5. */
  private widget(it: Item, box: [number, number, number, number]): void {
    const cls = STYLE_CLASS[it.type];
    const tb = it.sections.TabButton as Record<string, unknown> | undefined;
    const state = it.state ?? (cls === 'TABBUTTON' && tb?.m_bFirstTab ? 'Selected' : INITIAL_STATE[cls]);
    const st = this.ui.style(cls, it.style, state);
    const t = st?.texture ? this.ui.texture(st.texture) : null;
    if (t) this.texture(t, it.type === T_TICK ? [box[0], box[1], box[3], box[3]] : box, it.pColours);
    const sec = (it.sections.button ?? it.sections.TabButton ?? it.sections.tickbox ?? {}) as Record<string, unknown>;
    const title = it.text ?? this.ui.localise(sec.m_LocaliseTitleToken as string, sec.m_Title as string);
    if (!title || (st && st.showtitle === false)) return;
    const fc = st?.fontcolour;
    const col: Rgba = fc && !sec.m_bOverrideColour ? [fc[0] / 255, fc[1] / 255, fc[2] / 255, fc[3] / 255]
      : [Number(sec.m_TextColourR ?? 1), Number(sec.m_TextColourG ?? 1), Number(sec.m_TextColourB ?? 1), Number(sec.m_TextColourA ?? 1)];
    const align = st?.align === 'left' ? 0 : st?.align === 'right' ? 2 : 1;
    if (it.type === T_TICK) this.text(title, Number(sec.m_FontID ?? 0), [box[0] + box[3] + 6 * this.s, box[1], box[2] - box[3], box[3]], col, 0, true, false, 0, null);
    else if (it.type === T_BUTTON || it.type === T_CRAFT) {
      const w = 0.85 * box[2];
      this.text(title, Number(sec.m_FontID ?? 0), [box[0] + (box[2] - w) / 2, box[1] - 5 * this.s, w, box[3]], col, align, true, true, 0, null);
    } else this.text(title, Number(sec.m_FontID ?? 0), box, col, align, true, false, 0, null);
  }

  /** A flat or gradient quad: corners 0..3 = top left, top right, bottom left, bottom right. */
  poly(box: [number, number, number, number], c: Rgba[]): void {
    const ctx = this.ctx;
    const css = (v: Rgba): string => `rgba(${Math.round(v[0] * 255)},${Math.round(v[1] * 255)},${Math.round(v[2] * 255)},${v[3]})`;
    const same = (a: Rgba, b: Rgba): boolean => a.every((v, i) => Math.abs(v - b[i]) < 1e-3);
    if (same(c[0], c[1]) && same(c[0], c[2]) && same(c[0], c[3])) ctx.fillStyle = css(c[0]);
    else {
      const horizontal = same(c[0], c[2]) && same(c[1], c[3]);
      const g = horizontal ? ctx.createLinearGradient(box[0], 0, box[0] + box[2], 0) : ctx.createLinearGradient(0, box[1], 0, box[1] + box[3]);
      g.addColorStop(0, css(horizontal ? c[0] : c[0]));
      g.addColorStop(1, css(horizontal ? c[1] : c[2]));
      ctx.fillStyle = g;
    }
    ctx.fillRect(box[0], box[1], box[2], box[3]);
  }

  /** A texture stretched over the box (the packer's crop shrinks the quad to the kept area),
   * modulated by the item's colour. */
  texture(t: TexRef, box: [number, number, number, number], c: Rgba[]): void {
    let [left, top, bw, bh] = box;
    if (t.crop) {
      const [cx, cy, ow, oh] = t.crop;
      left += Math.trunc((cx * bw) / ow);
      top += Math.trunc((cy * bh) / oh);
      bw = (t.sw / ow) * bw;
      bh = (t.sh / oh) * bh;
    }
    const col = c[0];
    const ctx = this.ctx;
    const alpha = ctx.globalAlpha;
    ctx.globalAlpha = alpha * col[3];
    if (col[0] > 0.995 && col[1] > 0.995 && col[2] > 0.995) {
      ctx.drawImage(t.img, t.sx, t.sy, t.sw, t.sh, left, top, bw, bh);
    } else {
      ctx.drawImage(this.tinted(t, col), 0, 0, t.sw, t.sh, left, top, bw, bh);
    }
    ctx.globalAlpha = alpha;
  }

  /** The sprite multiplied by the colour, cached by sprite and the 8-bit colour the fill uses (a
   * colour animation within one 8-bit step reuses its canvas). */
  private tinted(t: TexRef, col: Rgba): HTMLCanvasElement {
    const r = Math.round(col[0] * 255), gr = Math.round(col[1] * 255), b = Math.round(col[2] * 255);
    const key = `${t.key}|${r},${gr},${b}`;
    let cv = this.tint.get(key);
    if (cv) return cv;
    if (this.tint.size > 512) this.tint.clear();
    cv = document.createElement('canvas');
    cv.width = Math.max(1, t.sw);
    cv.height = Math.max(1, t.sh);
    const g = cv.getContext('2d');
    if (g) {
      g.drawImage(t.img, t.sx, t.sy, t.sw, t.sh, 0, 0, t.sw, t.sh);
      g.globalCompositeOperation = 'multiply';
      g.fillStyle = `rgb(${r},${gr},${b})`;
      g.fillRect(0, 0, t.sw, t.sh);
      g.globalCompositeOperation = 'destination-in';
      g.drawImage(t.img, t.sx, t.sy, t.sw, t.sh, 0, 0, t.sw, t.sh);
    }
    this.tint.set(key, cv);
    return cv;
  }

  /** The CSS font of a font XID at the current scale, its design pixel size, the CSS font at that
   * size and the scale from design to canvas pixels. FM_Font makes its glyphs at the design size
   * (FM_Font::Initialise 0xf262d0: FT_Set_Pixel_Sizes(face, 0, m_PointSize)) and the item's
   * matrix scales them. */
  font(fontXid: number, scale = 1): { css: string; px: number; design: string; k: number } {
    const f = this.ui.font(fontXid);
    return { css: `${f.px * this.s * scale}px "${f.family}"`, px: f.px, design: `${f.px}px "${f.family}"`, k: this.s * scale };
  }

  /** A glyph's advance (design pixels): FM_Glyph::Initialise 0xf270d0's hinted FreeType advance
   * at the design size, a whole pixel (PrintWrappableAt 0xf250c0 adds trunc(0.5 + advance)). For
   * these fonts that is the rounded linear advance (FreeType checked for every ASCII glyph of
   * OrnitonsSerial, Blitzkrieg NF and WMD-SerifBold at 16, 20 and 24 px). A space (0x20, 0xa0) is
   * laid out as a blank 'l'; FM_Font::PrintAt adds the advances without kerning. */
  private advance(design: string, ch: string): number {
    const key = `${design}\u0000${ch}`;
    let a = this.advances.get(key);
    if (a === undefined) {
      const prev = this.ctx.font;
      this.ctx.font = design;
      a = Math.trunc(0.5 + this.ctx.measureText(ch === ' ' || ch === '\u00a0' ? 'l' : ch).width);
      this.ctx.font = prev;
      this.advances.set(key, a);
    }
    return a;
  }

  /** A run's width in design pixels. */
  private runWidth(design: string, s: string): number {
    let w = 0;
    for (const ch of s) w += this.advance(design, ch);
    return w;
  }

  /** The width (canvas pixels) FM_Font gives `text` in a font XID. */
  textWidth(text: string, fontXid: number, scale = 1): number {
    const f = this.font(fontXid, scale);
    return this.runWidth(f.design, text) * f.k;
  }

  /** The union of FM_Font::PrintAt 0xf24d30's glyph quads (design pixels, y up, from the first
   * glyph's pen on the baseline): each glyph its FreeType bitmap (FM_Glyph::Initialise 0xf270d0:
   * bitmap_left/top, width x rows; a space is a blank 'l') at the pen, the pen moved by the
   * advances. The bitmap edges are the ink box's rounded out. */
  inkBox(text: string, fontXid: number): { l: number; r: number; t: number; b: number } {
    const { design } = this.font(fontXid);
    const prev = this.ctx.font;
    this.ctx.font = design;
    let x = 0, l = Infinity, r = -Infinity, t = -Infinity, b = Infinity;
    for (const ch of text) {
      const m = this.ctx.measureText(ch === ' ' || ch === '\u00a0' ? 'l' : ch);
      l = Math.min(l, x + Math.floor(-m.actualBoundingBoxLeft));
      r = Math.max(r, x + Math.ceil(m.actualBoundingBoxRight));
      t = Math.max(t, Math.ceil(m.actualBoundingBoxAscent));
      b = Math.min(b, -Math.ceil(m.actualBoundingBoxDescent));
      x += this.advance(design, ch);
    }
    this.ctx.font = prev;
    return l <= r ? { l, r, t, b } : { l: 0, r: 0, t: 0, b: 0 };
  }

  /** One line glyph by glyph from the baseline at (x, y). */
  private run(design: string, k: number, s: string, x: number, y: number): void {
    for (const ch of s) {
      if (ch !== ' ' && ch !== '\u00a0') this.ctx.fillText(ch, x, y);
      x += this.advance(design, ch) * k;
    }
  }

  /** `FM_Font::m_Height` of a font XID in design pixels. */
  fontHeight(fontXid: number): number {
    const f = this.font(fontXid);
    return this.glyphHeight(f.css) / f.k;
  }

  /** A texture's kept pixels through the canvas transform `m` (canvas = (m0 x + m2 y + m4,
   * m1 x + m3 y + m5) of the sprite pixel (x, y)), modulated by the colour: an `XPRIM_QUAD` sub
   * with a matrix (game/widgets.ts). */
  affine(t: TexRef, m: [number, number, number, number, number, number], col: Rgba): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
    ctx.globalAlpha *= col[3];
    if (col[0] > 0.995 && col[1] > 0.995 && col[2] > 0.995) ctx.drawImage(t.img, t.sx, t.sy, t.sw, t.sh, 0, 0, t.sw, t.sh);
    else ctx.drawImage(this.tinted(t, col), 0, 0, t.sw, t.sh, 0, 0, t.sw, t.sh);
    ctx.restore();
  }

  /** `FM_Font::m_Height`: the tallest cached glyph (FM_FontManager::__GenTpage 0xf22660). */
  private glyphHeight(css: string): number {
    let h = this.heights.get(css);
    if (h === undefined) {
      this.ctx.font = css;
      const m = this.ctx.measureText('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789()[]|$@%&');
      h = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
      this.heights.set(css, h);
    }
    return h;
  }

  /** Text in a box (canvas pixels) as FM_Font::PrintAt / PrintWrappableAt place it. */
  text(text: string, fontXid: number, box: [number, number, number, number], col: Rgba, align: number, valign: boolean,
    wrap: boolean, lineSpace: number, shadow: { dx: number; dy: number; col: Rgba } | null, scale = 1): void {
    const ctx = this.ctx;
    const { css, design, k } = this.font(fontXid, scale);
    ctx.font = css;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    const [left, top, bw, bh] = box;
    const lines: string[] = [];
    for (const para of text.replace(/\\n/g, '\n').split('\n')) {
      if (!wrap) { lines.push(para); continue; }
      let cur = '';
      for (const word of para.split(' ')) {
        const cand = cur ? `${cur} ${word}` : word;
        // PrintWrappableAt 0xf250c0 breaks once the line's width reaches the box (>=).
        if (cur && this.runWidth(design, cand) >= bw / k - 1e-3) { lines.push(cur); cur = word; } else cur = cand;
      }
      lines.push(cur);
    }
    const gh = this.glyphHeight(css);
    const lh = gh + lineSpace * this.s;
    let base = valign ? top + bh / 2 + gh / 2 : top + bh;
    if (wrap && lines.length > 1) base -= (lines.length - 1) * lh * (valign ? 0.5 : 1);
    const css4 = (v: Rgba): string => `rgba(${Math.round(v[0] * 255)},${Math.round(v[1] * 255)},${Math.round(v[2] * 255)},${v[3]})`;
    lines.forEach((line, n) => {
      const tw = this.runWidth(design, line) * k;
      const x = align === 1 ? Math.trunc(left + bw / 2 - tw / 2) : align === 2 ? Math.trunc(left + bw - tw) : left;
      const y = base + n * lh;
      if (shadow) {
        ctx.fillStyle = css4(shadow.col);
        this.run(design, k, line, x + shadow.dx * this.s, y + shadow.dy * this.s);
      }
      ctx.fillStyle = css4(col);
      this.run(design, k, line, x, y);
    });
  }
}
